import AppRegistrationIcon from '@mui/icons-material/AppRegistrationOutlined';
import AltRouteIcon from '@mui/icons-material/AltRouteOutlined';
import AccountBalanceIcon from '@mui/icons-material/AccountBalanceOutlined';
import TuneIcon from '@mui/icons-material/TuneOutlined';
import SmartToyIcon from '@mui/icons-material/SmartToyOutlined';
import ExploreIcon from '@mui/icons-material/ExploreOutlined';
import WorkspacesIcon from '@mui/icons-material/WorkspacesOutlined';
import HubIcon from '@mui/icons-material/HubOutlined';
import GridOnIcon from '@mui/icons-material/GridOnOutlined';
import DnsIcon from '@mui/icons-material/DnsOutlined';
import LeaderboardIcon from '@mui/icons-material/LeaderboardOutlined';
import MapIcon from '@mui/icons-material/MapOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAltOutlined';
import LightbulbIcon from '@mui/icons-material/LightbulbOutlined';
import DatasetIcon from '@mui/icons-material/DatasetOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLongOutlined';
import InsightsIcon from '@mui/icons-material/InsightsOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import WidgetsIcon from '@mui/icons-material/WidgetsOutlined';
import StorageIcon from '@mui/icons-material/StorageOutlined';
import Inventory2Icon from '@mui/icons-material/Inventory2Outlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import ConstructionIcon from '@mui/icons-material/Construction';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import GridViewIcon from '@mui/icons-material/GridViewOutlined';
import MenuBookIcon from '@mui/icons-material/MenuBookOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlineOutlined';
import FolderOpenIcon from '@mui/icons-material/FolderOpenOutlined';
import SchemaIcon from '@mui/icons-material/SchemaOutlined';
import RequestQuoteIcon from '@mui/icons-material/RequestQuoteOutlined';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
// Executive Dashboard (Project Management, 2026-09-30). A ligature with no entry here
// renders NO icon at all and the label slides left, out of line with every other row —
// which is exactly what happened the first time this menu was added.
import QueryStatsIcon from '@mui/icons-material/QueryStatsOutlined';
// Application Evaluation (#6). Same rule as the rest of this list: the key is the
// SOURCE's Material Icons ligature, so a ported navConfig entry needs no translation.
import FactCheckIcon from '@mui/icons-material/FactCheckOutlined';
import ModelTrainingIcon from '@mui/icons-material/ModelTrainingOutlined';
import PlayCircleIcon from '@mui/icons-material/PlayCircleOutlined';
import RuleIcon from '@mui/icons-material/RuleOutlined';
import AssignmentIcon from '@mui/icons-material/AssignmentOutlined';
import QuizIcon from '@mui/icons-material/QuizOutlined';
import AnalyticsIcon from '@mui/icons-material/AnalyticsOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import PsychologyIcon from '@mui/icons-material/PsychologyOutlined';
import TravelExploreIcon from '@mui/icons-material/TravelExploreOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesomeOutlined';
import VerifiedIcon from '@mui/icons-material/VerifiedOutlined';
import ManageSearchIcon from '@mui/icons-material/ManageSearchOutlined';
import OutboundIcon from '@mui/icons-material/OutboundOutlined';
import LockIcon from '@mui/icons-material/LockOutlined';
// Project Execution & Project Management. `event_note` and `slideshow` are the SOURCE's own
// ligatures for the two meeting-deck screens (`_DECK_CFG`, roadmap-execution.js:544) and
// `rocket_launch` is the one its nav gives the Program Management bucket (layout-v2.ejs:114);
// the rest follow the same convention for rows the source's menu leaves unlabelled.
import GroupsIcon from '@mui/icons-material/GroupsOutlined';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunchOutlined';
import HowToRegIcon from '@mui/icons-material/HowToRegOutlined';
import MonitoringIcon from '@mui/icons-material/MonitorHeartOutlined';
import CrisisAlertIcon from '@mui/icons-material/CrisisAlertOutlined';
import AccountTreeIcon from '@mui/icons-material/AccountTreeOutlined';
import EditNoteIcon from '@mui/icons-material/EditNoteOutlined';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import ChecklistIcon from '@mui/icons-material/ChecklistOutlined';
import PriceCheckIcon from '@mui/icons-material/PriceCheckOutlined';
import TimelineIcon from '@mui/icons-material/TimelineOutlined';
import EventIcon from '@mui/icons-material/EventOutlined';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportantOutlined';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import EventNoteIcon from '@mui/icons-material/EventNoteOutlined';
import SlideshowIcon from '@mui/icons-material/SlideshowOutlined';
// Program Planning & Roadmap — the source's Planning & Integration navConfig icons.
import EngineeringIcon from '@mui/icons-material/EngineeringOutlined';
import ShareIcon from '@mui/icons-material/ShareOutlined';
import SavingsIcon from '@mui/icons-material/SavingsOutlined';
import BadgeIcon from '@mui/icons-material/BadgeOutlined';
import AssignmentIndIcon from '@mui/icons-material/AssignmentIndOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUpOutlined';
import type { SvgIconComponent } from '@mui/icons-material';

/**
 * Maps the logical icon names used by `@platform/entitlements` to Material icons.
 *
 * The mapping lives here rather than in entitlements so that package stays free of
 * any UI dependency and can run on the server.
 */
const MODULE_ICONS: Record<string, SvgIconComponent> = {
  apr: AppRegistrationIcon,
  ai_readiness: SmartToyIcon,
  erp_consolidation: HubIcon,
  spn: ExploreIcon,
  portfolio_definition: WorkspacesIcon,
  program_planning: MapIcon,
  project_management: TaskAltIcon,
  strategic_intelligence: LightbulbIcon,
  master_data: DatasetIcon,
  reporting: InsightsIcon,
  licensing_usage: ReceiptLongIcon,
  admin: SettingsIcon,
};

/**
 * Icons for individual nav rows, keyed by the SOURCE APP's Material Icons ligature.
 *
 * The source puts an icon on every nav row and names it with the ligature it passes to
 * `<i className="material-icons">`. Keeping those exact names as the keys means a ported
 * `navConfig` entry can be copied across unchanged, and the mapping to a React icon
 * component stays here where the UI dependency belongs.
 */
const SECTION_ICONS: Record<string, SvgIconComponent> = {
  storage: StorageIcon,
  inventory_2: Inventory2Icon,
  attach_money: AttachMoneyIcon,
  settings: SettingsOutlinedIcon,
  dashboard: DashboardOutlinedIcon,
  construction: ConstructionIcon,
  dashboard_customize: DashboardCustomizeIcon,
  grid_view: GridViewIcon,
  menu_book: MenuBookIcon,
  help_outline: HelpOutlineIcon,
  folder_open: FolderOpenIcon,
  schema: SchemaIcon,
  request_quote: RequestQuoteIcon,
  history: HistoryIcon,
  // Application Evaluation. These thirteen names were used in the catalogue when #6
  // landed but never mapped here, so `getSectionIcon` returned undefined and the whole
  // bucket — its own row and every child — rendered with no icon while its neighbours
  // had them. `inventory_2` and `menu_book` are reused from above.
  fact_check: FactCheckIcon,
  model_training: ModelTrainingIcon,
  play_circle: PlayCircleIcon,
  rule: RuleIcon,
  assignment: AssignmentIcon,
  quiz: QuizIcon,
  analytics: AnalyticsIcon,
  download: DownloadIcon,
  psychology: PsychologyIcon,
  // SPN → Discoveries. The catalogue named this ligature and the map did not carry it,
  // so the section rendered with no icon at all.
  travel_explore: TravelExploreIcon,
  auto_awesome: AutoAwesomeIcon,
  insights: InsightsIcon,
  verified: VerifiedIcon,
  manage_search: ManageSearchIcon,
  // SPN → Transfer. Registered ALONGSIDE the catalogue entry that names it, because the
  // two have drifted apart twice already: a name the catalogue uses and this map does not
  // carry renders no icon at all, silently, and the row looks broken next to its
  // neighbours. `lock` is here as the alternative — the act versus its consequence.
  outbound: OutboundIcon,
  lock: LockIcon,
  // Inventory Registry. These four are the Material icons the SOURCE menu uses
  // (layout-v2.ejs:77-103) — `lightbulb` for Opportunities, `alt_route` for the two
  // delivery pathways, `account_balance` for Financial Modeling, `tune` for the
  // Optimizer. The owner's brief keeps the source's menu content and applies THIS
  // platform's menu design, so the icons come across and the styling does not.
  lightbulb: LightbulbIcon,
  alt_route: AltRouteIcon,
  account_balance: AccountBalanceIcon,
  tune: TuneIcon,
  grid_on: GridOnIcon,
  // ERP Consolidation's System Selection. Registered in the SAME change as its catalogue entry
  // — the sixth time this file records the lesson, and it has not changed: a ligature the
  // catalogue names and this map does not carry renders NO icon at all, silently, and shifts
  // the nav label left where the icon should have been.
  dns: DnsIcon,
  leaderboard: LeaderboardIcon,
  // Project Execution & Project Management. Registered ALONGSIDE the catalogue entries that
  // name them, for the reason the SPN comment above records: a ligature the catalogue uses
  // and this map does not carry renders no icon at all, silently.
  groups: GroupsIcon,
  rocket_launch: RocketLaunchIcon,
  how_to_reg: HowToRegIcon,
  monitoring: MonitoringIcon,
  query_stats: QueryStatsIcon,
  // Portfolio Exceptions. Registered in the SAME change as its catalogue entry — the fourth
  // time this file records the lesson, and the reason is unchanged: a ligature the catalogue
  // names and this map does not carry renders no icon at all, and nothing reports it.
  crisis_alert: CrisisAlertIcon,
  // Portfolio / Project Setup (§14.1.2). Registered in the SAME change as the catalogue
  // entry — the fifth time this file records that lesson, and the reason is unchanged.
  account_tree: AccountTreeIcon,
  edit_note: EditNoteIcon,
  record_voice_over: RecordVoiceOverIcon,
  checklist: ChecklistIcon,
  price_check: PriceCheckIcon,
  timeline: TimelineIcon,
  description: DescriptionIcon,
  event_note: EventNoteIcon,
  slideshow: SlideshowIcon,
  task_alt: TaskAltIcon,
  /*
    ROADMAP DEFINITION. Registered ALONGSIDE the catalogue entries that name them — the third
    time this file records the same lesson, and the reason the group rendered with no icon at
    all next to neighbours that had them: `getSectionIcon` returns undefined for an unmapped
    ligature and the row simply loses its icon, silently.

    `event` is here for Timeline Finalization, whose catalogue entry lands with its screen.
  */
  map: MapIcon,
  workspaces: WorkspacesIcon,
  event: EventIcon,
  /*
    Action Needed. Registered IN THE SAME CHANGE as the catalogue entry that names it —
    which is the discipline the notes above were written for, and which I still managed to
    miss: the entry shipped first and the leaf rendered with no icon at all, silently, next
    to neighbours that had them. Nothing fails, nothing warns; the row just looks broken.
  */
  notification_important: NotificationImportantIcon,
  // Program Planning & Roadmap (catalog.ts program_planning entry).
  engineering: EngineeringIcon,
  share: ShareIcon,
  savings: SavingsIcon,
  badge: BadgeIcon,
  assignment_ind: AssignmentIndIcon,
  trending_up: TrendingUpIcon,
};

export function getModuleIcon(name: string): SvgIconComponent {
  return MODULE_ICONS[name] ?? WidgetsIcon;
}

/** Returns undefined for an unmapped name, so a row renders no icon rather than a guess. */
export function getSectionIcon(name: string | undefined): SvgIconComponent | undefined {
  return name ? SECTION_ICONS[name] : undefined;
}

export type { SvgIconComponent };
